@if (isLoading()) {
  <div class="sav-dp-wrap">
    <div class="sav-dp-skel sav-dp-skel-crumbs"></div>
    <div class="sav-dp-skel sav-dp-skel-hero"></div>
    <div class="sav-dp-skel sav-dp-skel-line"></div>
    <div class="sav-dp-skel sav-dp-skel-block"></div>
  </div>
} @else if (notFound()) {
  <div class="sav-dp-empty">
    <h1>Page not found</h1>
    <p>
      This discount page is not available. It may have been unpublished or the address is wrong.
    </p>
    <a routerLink="/storelist" class="sav-dp-btn">Browse all stores</a>
  </div>
} @else if (page(); as data) {
  <div class="sav-dp-wrap">
    <app-breadcrumb [items]="crumbs()" />

    <!-- Hero. Store identity comes from the live store record, so it follows the store. -->
    <section class="sav-dp-hero" aria-label="Discount overview">
      <div class="sav-dp-hero-store">
        <span class="sav-dp-logo">
          @if (data.store.logoImagePath) {
            <img [src]="data.store.logoImagePath | assetUrl" [alt]="data.store.name" />
          } @else {
            <span class="sav-dp-logo-initial">{{ data.store.name.charAt(0) }}</span>
          }
        </span>
        <span class="sav-dp-hero-store-meta">
          <span class="sav-dp-eyebrow">{{ data.discountTypeLabel }}</span>
          @if (storePath()) {
            <a [routerLink]="storePath()" class="sav-dp-hero-store-name">{{ data.store.name }}</a>
          } @else {
            <span class="sav-dp-hero-store-name">{{ data.store.name }}</span>
          }
        </span>
      </div>

      <h1>{{ data.headingH1 || data.title || data.store.name + ' ' + data.discountTypeLabel }}</h1>

      @if (data.shortDescription) {
        <div class="sav-dp-lede" [innerHTML]="data.shortDescription | trustedCmsHtml"></div>
      }

      <!-- Trust pills. Each is rendered only when the underlying fact is real. -->
      <div class="sav-dp-pills">
        @if (hasVerifiedOffer()) {
          <span class="sav-dp-pill"
            ><span class="sav-dp-tick" aria-hidden="true">✓</span> Verified offers</span
          >
        }
        @if (offerCountLabel()) {
          <span class="sav-dp-pill">{{ offerCountLabel() }}</span>
        }
        @if (hasRating()) {
          <span class="sav-dp-pill">
            <span class="sav-dp-star" aria-hidden="true">★</span>
            {{ ratingValue() }} store rating
          </span>
        }
        @if (lastCheckedLabel()) {
          <span class="sav-dp-pill">Updated {{ lastCheckedLabel() }}</span>
        }
      </div>

      <!-- The page's primary action belongs with the summary, not only at the foot of the
           sidebar where it sat below the fold. -->
      @if (data.store.storeUrl || data.store.affiliateLink) {
        <div class="sav-dp-hero-actions">
          <a
            class="sav-dp-btn"
            [href]="data.store.affiliateLink || data.store.storeUrl"
            target="_blank"
            rel="nofollow sponsored noopener"
            >Visit {{ data.store.name }}</a
          >
          @if (storePath()) {
            <a class="sav-dp-btn is-ghost" [routerLink]="storePath()"
              >All {{ data.store.name }} coupons</a
            >
          }
        </div>
      }
    </section>

    <div class="sav-dp-layout">
      <div class="sav-dp-main">
        <!-- Says plainly that nothing is live rather than leaving a gap where offers would be. -->
        @if (hasNoOffers()) {
          <section class="sav-dp-empty-offers" aria-label="Current offers">
            <h2>No {{ data.discountTypeLabel.toLowerCase() }} code is live right now</h2>
            <p>
              We check {{ data.store.name }} regularly and list any
              {{ data.discountTypeLabel.toLowerCase() }}
              here as soon as one runs. In the meantime its general coupons are often the better
              saving.
            </p>
            @if (storePath()) {
              <a class="sav-dp-btn" [routerLink]="storePath()"
                >See all {{ data.store.name }} coupons</a
              >
            }
          </section>
        }

        @if (coupons().length > 0) {
          <section class="sav-dp-section" aria-labelledby="dp-codes-heading">
            <app-section-heading
              [heading]="
                'Today\'s ' +
                data.store.name +
                ' ' +
                data.discountTypeLabel.toLowerCase() +
                ' codes'
              "
              [subtitle]="
                'Live ' + data.store.name + ' codes, updated from the store\'s coupon page.'
              "
            />

            <!-- The site's full coupon card (shared/coupon-card), the same one the store page
                 lists — discount badge, badges, description and the Get Deal / Show Code button. -->
            <div class="sav-offer-list">
              @for (coupon of couponCards(); track coupon.id) {
                <app-coupon-card [card]="coupon" (action)="openOffer($event)" />
              }
            </div>
          </section>
        }

        @for (section of sections(); track section.heading) {
          <section class="sav-dp-section">
            <app-section-heading [heading]="section.heading" [subtitle]="section.subtitle" />
            <div class="sav-dp-prose" [innerHTML]="section.html | trustedCmsHtml"></div>
          </section>
        }

        @if (howToClaim()) {
          <section class="sav-dp-section" aria-labelledby="dp-steps-heading">
            <app-section-heading
              [heading]="
                'How to get your ' + data.store.name + ' ' + data.discountTypeLabel.toLowerCase()
              "
              subtitle="Four steps, from finding the code to seeing the total drop."
            />
            <!-- An ordered list here renders as the numbered stepper; see .sav-dp-steps in the SCSS. -->
            <div
              class="sav-dp-prose sav-dp-steps"
              [innerHTML]="howToClaim() | trustedCmsHtml"
            ></div>
          </section>
        }

        @if (data.faqs.length > 0) {
          <section class="sav-dp-section" aria-labelledby="dp-faq-heading">
            <app-section-heading
              heading="Frequently asked questions"
              [subtitle]="
                'What shoppers ask about the ' +
                data.store.name +
                ' ' +
                data.discountTypeLabel.toLowerCase() +
                '.'
              "
            />
            <!-- Shared FAQ accordion — see `.sav-faq-*` in styles.scss. -->
            <div class="sav-faq-list">
              @for (faq of data.faqs; track faq.question; let i = $index) {
                <div class="sav-faq-item" [class.expanded]="expandedFaq() === i">
                  <h3 class="sav-faq-heading">
                    <button
                      type="button"
                      class="sav-faq-question"
                      [attr.aria-expanded]="expandedFaq() === i"
                      [attr.aria-controls]="'dp-faq-a-' + i"
                      (click)="toggleFaq(i)"
                    >
                      {{ faq.question }}
                    </button>
                  </h3>
                  <div class="sav-faq-answer" [class.visible]="expandedFaq() === i">
                    <div
                      class="sav-faq-answer-body"
                      [id]="'dp-faq-a-' + i"
                      [innerHTML]="faq.answer | trustedCmsHtml"
                    ></div>
                  </div>
                </div>
              }
            </div>
          </section>
        }

        @if (deals().length > 0) {
          <section class="sav-dp-section" aria-labelledby="dp-deals-heading">
            <app-section-heading
              [heading]="'More ' + data.store.name + ' deals — no code needed'"
              subtitle="Offers that apply at checkout without a code."
            />
            <div class="sav-offer-list">
              @for (deal of dealCards(); track deal.id) {
                <app-coupon-card [card]="deal" (action)="openOffer($event)" />
              }
            </div>
          </section>
        }

        <!-- Other stores running the same discount, this store's category first. Each tile opens
             that store's own discount page. Rendered only when such pages exist — there is no
             placeholder here. -->
        @if (relatedStores().length > 0) {
          <section class="sav-dp-section" aria-label="Other stores with this discount">
            <app-section-heading
              [heading]="relatedStoresHeading()"
              [subtitle]="relatedStoresSubtitle()"
            />
            <div class="sav-dp-store-grid">
              @for (related of relatedStores(); track related.slug) {
                <a class="sav-dp-store-tile" [routerLink]="'/' + related.slug">
                  <span class="sav-dp-store-logo">
                    @if (related.storeLogoImagePath) {
                      <img
                        [src]="related.storeLogoImagePath | assetUrl"
                        [alt]="related.storeName"
                        loading="lazy"
                      />
                    } @else {
                      {{ related.storeName.charAt(0) }}
                    }
                  </span>
                  <span class="sav-dp-store-meta">
                    <span class="sav-dp-store-name">{{ related.storeName }}</span>
                    <span class="sav-dp-store-type">{{ data.discountTypeLabel }}</span>
                  </span>
                </a>
              }
            </div>
          </section>
        }
      </div>

      <aside class="sav-dp-side" aria-label="Discount summary">
        @if (detailRows().length > 0) {
          <section class="sav-dp-card">
            <h2 class="sav-dp-card-title">Discount details</h2>
            <dl class="sav-dp-rows">
              @for (row of detailRows(); track row.label) {
                <div class="sav-dp-row">
                  <dt>{{ row.label }}</dt>
                  <dd [class.is-accent]="row.accent">{{ row.value }}</dd>
                </div>
              }
            </dl>
            @if (data.store.storeUrl) {
              <a
                class="sav-dp-btn sav-dp-btn-block"
                [href]="data.store.affiliateLink || data.store.storeUrl"
                target="_blank"
                rel="nofollow sponsored noopener"
                >Visit {{ data.store.name }}</a
              >
            }
          </section>
        }

        @if (hasRating()) {
          <section class="sav-dp-card sav-dp-rating-card">
            <h2 class="sav-dp-card-title">Store rating</h2>
            <p class="sav-dp-rating-value">{{ ratingValue() }}</p>
            <p class="sav-dp-stars" [attr.aria-label]="ratingValue() + ' out of 5'">
              @for (filled of ratingStars(); track $index) {
                <span aria-hidden="true" [class.is-empty]="!filled">★</span>
              }
            </p>
            <p class="sav-dp-rating-caption">
              Based on {{ ratingCount() }} shopper {{ ratingCount() === 1 ? 'vote' : 'votes' }} for
              {{ data.store.name }}.
            </p>
          </section>
        }

        <!-- Every page of a store links to the store's coupon page and to its other discount
             pages, so the whole set is reachable from any one of them. -->
        <section class="sav-dp-card">
          <h2 class="sav-dp-card-title">More from {{ data.store.name }}</h2>
          <ul class="sav-dp-related">
            @if (storePath()) {
              <li>
                <a [routerLink]="storePath()">
                  <span class="sav-dp-related-logo">
                    @if (data.store.logoImagePath) {
                      <img
                        [src]="data.store.logoImagePath | assetUrl"
                        [alt]="data.store.name"
                        loading="lazy"
                      />
                    } @else {
                      {{ data.store.name.charAt(0) }}
                    }
                  </span>
                  <span class="sav-dp-related-name">All {{ data.store.name }} coupons</span>
                </a>
              </li>
            }
            @for (sibling of storePages(); track sibling.slug) {
              <li>
                <a [routerLink]="'/' + sibling.slug">
                  <span class="sav-dp-related-logo" aria-hidden="true">%</span>
                  <span class="sav-dp-related-name">{{ sibling.discountTypeLabel }}</span>
                </a>
              </li>
            }
          </ul>
        </section>

        @if (relatedPages().length > 0) {
          <section class="sav-dp-card">
            <h2 class="sav-dp-card-title">More {{ data.discountTypeLabel.toLowerCase() }}s</h2>
            <ul class="sav-dp-related">
              @for (related of relatedPages(); track related.slug) {
                <li>
                  <a [routerLink]="'/' + related.slug">
                    <span class="sav-dp-related-logo">
                      @if (related.storeLogoImagePath) {
                        <img
                          [src]="related.storeLogoImagePath | assetUrl"
                          [alt]="related.storeName"
                          loading="lazy"
                        />
                      } @else {
                        {{ related.storeName.charAt(0) }}
                      }
                    </span>
                    <span class="sav-dp-related-name">{{ related.storeName }}</span>
                  </a>
                </li>
              }
            </ul>
          </section>
        }
      </aside>
    </div>
  </div>
}
